import React, { Component } from 'react';
import { Form, Input, notification, Tabs } from 'antd';
import _ from 'lodash';
import { FormComponentProps } from 'antd/es/form';
import { connect } from 'react-redux';
import { dispatch, IRootDispatch } from 'stores/rematch/root-store';
import { SubTitle, Text } from 'common-components/typography';
import { ICustomer } from 'interfaces/customer-interfaces';
import ActionModal, { ActionModalFooter } from 'common-components/modal/ActionModal';
import { GhostButton, PrimaryButton, SecondaryButton } from 'common-components/buttons';
import CareLevelSelector from 'views/customers/details/components/CareLevelSelector';
import Utils from 'utilities/Utils';
import { AlertLevel, CareInformationType } from 'utilities/enum-utils';

const { TabPane } = Tabs;
const { TextArea } = Input;

interface ICreateEditBehaviourModalProps extends FormComponentProps {
  closeCreateEditBehaviourModal: () => void;
  isOpen: boolean;
  isNew: boolean;
  selectedCustomer: ICustomer;
  selectedBehaviour: any;
  doCreateCustomerCareInfo: typeof dispatch.customersStore.doCreateCustomerCareInfo;
  doUpdateCustomerCareInfo: typeof dispatch.customersStore.doUpdateCustomerCareInfo;
}

interface ICreateEditBehaviourModalState {
  isLoadingFilter: boolean;
  isActionModalOpen: boolean;
  selectedLevel: string;
  isLoading: boolean;
}

class CreateEditBehaviourModal extends Component<ICreateEditBehaviourModalProps, ICreateEditBehaviourModalState> {
  state = {
    isLoadingFilter: false,
    isActionModalOpen: false,
    selectedLevel: '',
    isLoading: false,
  };

  private _closeWithActionModal = () => {
    const { form, selectedBehaviour, isNew } = this.props;
    const { selectedLevel } = this.state;
    let hasChanged = false;
    const formValues = form.getFieldsValue();

    // Check if it's an Add or Edit modal
    if (isNew) {
      if (
        selectedLevel ||
        !Utils.isEmpty(formValues.behaviourName) ||
        !Utils.isEmpty(formValues.description) ||
        !Utils.isEmpty(formValues.managementStrategy)
      ) {
        hasChanged = true;
      }
    } else if (
      formValues.behaviourName !== selectedBehaviour.name ||
      selectedLevel !== AlertLevel[selectedBehaviour.alertLevel] ||
      formValues.managementStrategy !== selectedBehaviour.additionalInfo.managementStrategy ||
      formValues.description !== selectedBehaviour.description
    ) {
      hasChanged = true;
    }

    if (hasChanged) {
      this.setState({ isActionModalOpen: true });
    } else {
      this._closeActionCreateModal();
    }
  };

  private _closeActionCreateModal = () => {
    this.setState({
      isActionModalOpen: false,
      isLoadingFilter: false,
      isLoading: false,
      selectedLevel: _.isEmpty(this.props.selectedBehaviour) ? '' : AlertLevel[this.props.selectedBehaviour.alertLevel],
    });
    this.props.closeCreateEditBehaviourModal();
  };

  private _changeLevel = (level) => {
    const { form } = this.props;
    form.setFieldsValue({ alertLevel: level });
    this.setState({ selectedLevel: level });
  };

  private _submitBehaviour = async () => {
    const { form, doCreateCustomerCareInfo, doUpdateCustomerCareInfo, selectedBehaviour, selectedCustomer, isNew } =
      this.props;
    const { selectedLevel } = this.state;

    this.setState({ isLoading: true });

    let isFormValid = true;
    form.validateFields((err) => {
      if (err) {
        isFormValid = false;
      }
    });

    if (isFormValid) {
      const formValues = form.getFieldsValue();
      try {
        const payload = isNew
          ? {
              careInformationType: CareInformationType.BEHAVIOUR,
              alertLevel: AlertLevel[selectedLevel],
              name: formValues.behaviourName,
              managementStrategy: formValues.managementStrategy,
              description: formValues.description,
              userId: selectedCustomer.userId,
            }
          : {
              careInformationType: CareInformationType.BEHAVIOUR,
              alertLevel: AlertLevel[selectedLevel],
              careInformationId: selectedBehaviour.careInformationId,
              name: formValues.behaviourName,
              managementStrategy: formValues.managementStrategy,
              description: formValues.description,
              userId: selectedCustomer.userId,
            };
        isNew ? await doCreateCustomerCareInfo(payload) : await doUpdateCustomerCareInfo(payload);
        notification.success({ message: `Behaviour ${isNew ? 'added' : 'updated'} successfully.` });
        this._closeActionCreateModal();
      } catch (e) {
        notification.error({ message: 'Oops something went wrong! Please try again.' });
      }
    }

    this.setState({ isLoading: false });
  };

  componentDidUpdate(
    prevProps: Readonly<ICreateEditBehaviourModalProps>,
    prevState: Readonly<ICreateEditBehaviourModalState>,
    snapshot?: any,
  ) {
    if (prevProps.selectedBehaviour !== this.props.selectedBehaviour) {
      this.setState({
        selectedLevel: this.props.selectedBehaviour ? AlertLevel[this.props.selectedBehaviour.alertLevel] : '',
      });
    }
  }

  render() {
    const { form, selectedBehaviour, isNew } = this.props;
    const { selectedLevel, isLoading } = this.state;
    const { getFieldDecorator } = form;

    return (
      <div>
        <ActionModal
          isOpen={this.state.isActionModalOpen}
          onClose={this._closeActionCreateModal}
          title={'Discard changes'}
          showCloseButton={true}
        >
          <Text className={'mb-medium'}>
            You have <b>unsaved data</b>, proceeding will discard these changes.
          </Text>
          <br />
          <Text className={'mb-medium'}>Do you want to proceed?</Text>
          <ActionModalFooter>
            <PrimaryButton
              className="mr-medium"
              size="large"
              onClick={() => {
                this.setState({ isActionModalOpen: false });
              }}
            >
              Cancel
            </PrimaryButton>
            <GhostButton size="large" onClick={this._closeActionCreateModal}>
              Proceed
            </GhostButton>
          </ActionModalFooter>
        </ActionModal>
        <ActionModal
          isOpen={this.props.isOpen}
          onClose={this._closeWithActionModal}
          title={
            isNew ? (
              <>
                Add a <b>Behaviour</b>
              </>
            ) : (
              <>
                Edit a <b>Behaviour</b>
              </>
            )
          }
          showCloseButton={true}
          verticalAlignment={'center'}
          width={'medium'}
        >
          {isNew && (
            <div className="mb-large">
              <Text>
                Search and select a <b>behaviour</b> from the list provided.
              </Text>
            </div>
          )}
          <div className="mb-large">
            <SubTitle>Behaviour</SubTitle>
            <Form.Item className="mb-large">
              {getFieldDecorator('behaviourName', {
                rules: [{ required: true, message: 'Behaviour name required' }],
                initialValue: selectedBehaviour ? selectedBehaviour.name : null,
              })(<Input placeholder="Enter behaviour here" style={{ width: '400px' }} />)}
            </Form.Item>
          </div>
          <div className="mb-large">
            <div className="flex-row align-center mb-x-small">
              <SubTitle>ALERT LEVEL</SubTitle>
              <Text className="ml-x-small" size="regular">
                (Select one)
              </Text>
            </div>
            <CareLevelSelector selectedLevel={selectedLevel} changeLevel={this._changeLevel} />
            <Form.Item className="p-none">
              {getFieldDecorator('alertLevel', {
                rules: [{ required: true, message: 'Choose one alert level' }],
                initialValue: selectedLevel,
              })(<Input type="hidden" />)}
            </Form.Item>
          </div>
          <div className="mb-x-large">
            <Tabs defaultActiveKey={'description'} type="card" animated={true}>
              <TabPane tab="Description" key="description">
                <div
                  className="bordered-bottom bordered-right bordered-left ph-medium pv-small"
                  style={{ marginTop: '-17px' }}
                >
                  <div className="flex-row align-center mb-x-small">
                    <SubTitle>Description</SubTitle>
                    <Text className="ml-x-small" size="regular">
                      (Optional)
                    </Text>
                  </div>
                  <Form.Item className="mb-large">
                    {getFieldDecorator('description', {
                      rules: [],
                      initialValue: selectedBehaviour ? selectedBehaviour.description : null,
                    })(
                      <TextArea
                        placeholder={'Describe this behaviour, or leave a remark about this behaviour here.'}
                      />,
                    )}
                  </Form.Item>
                </div>
              </TabPane>
              <TabPane tab="Management Strategy" key="managementStrategy">
                <div
                  className="bordered-bottom bordered-right bordered-left ph-medium pv-small"
                  style={{ marginTop: '-17px' }}
                >
                  <div className="flex-row align-center mb-x-small">
                    <SubTitle>Management Strategy</SubTitle>
                    <Text className="ml-x-small" size="regular">
                      (Optional)
                    </Text>
                  </div>
                  <Form.Item className="mb-large">
                    {getFieldDecorator('managementStrategy', {
                      rules: [],
                      initialValue: _.get(selectedBehaviour, 'additionalInfo.managementStrategy', null),
                    })(<TextArea placeholder={'What strategies are suggested etc.'} />)}
                  </Form.Item>
                </div>
              </TabPane>
            </Tabs>
          </div>
          <ActionModalFooter>
            <SecondaryButton className="mr-medium" size="large" onClick={this._closeWithActionModal}>
              Cancel
            </SecondaryButton>
            <PrimaryButton size="large" onClick={this._submitBehaviour} loading={isLoading}>
              {isNew ? 'Add Behaviour' : 'Save Behaviour'}
            </PrimaryButton>
          </ActionModalFooter>
        </ActionModal>
      </div>
    );
  }
}

const mapDispatch = (dispatch: IRootDispatch) => ({
  doCreateCustomerCareInfo: dispatch.customersStore.doCreateCustomerCareInfo,
  doUpdateCustomerCareInfo: dispatch.customersStore.doUpdateCustomerCareInfo,
});

export default connect(null, mapDispatch)(Form.create<ICreateEditBehaviourModalProps>()(CreateEditBehaviourModal));
